/**
 * Color-to-alpha "magic wand" for non-tech-savvy users who upload a frame
 * with a solid background instead of a transparent PNG. We sample a pixel
 * they pick, then make pixels within a tolerance of that color transparent.
 *
 * Pure-canvas, runs on the main thread — no deps.
 */

export interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

export async function loadImageBitmap(file: File): Promise<ImageBitmap> {
  return await createImageBitmap(file);
}

/** Sample the pixel at the given normalized [0..1] coordinate. */
export function samplePixel(
  bitmap: ImageBitmap,
  ux: number,
  uy: number,
): Rgba {
  const canvas = document.createElement('canvas');
  canvas.width = bitmap.width;
  canvas.height = bitmap.height;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(bitmap, 0, 0);
  const x = Math.min(bitmap.width - 1, Math.max(0, Math.round(ux * bitmap.width)));
  const y = Math.min(bitmap.height - 1, Math.max(0, Math.round(uy * bitmap.height)));
  const d = ctx.getImageData(x, y, 1, 1).data;
  return { r: d[0]!, g: d[1]!, b: d[2]!, a: d[3]! };
}

export interface RemoveColorOptions {
  /** 0..255 — pixels within this Euclidean distance of `target` are removed. */
  tolerance: number;
  /** Optional fall-off width (0..255) for a soft edge. 0 = hard edge. */
  feather: number;
}

/**
 * Returns a NEW canvas (HTMLCanvasElement) the same size as the input, with
 * pixels matching `target` made transparent.
 */
export function removeColor(
  source: ImageBitmap | HTMLCanvasElement,
  target: Rgba,
  opts: RemoveColorOptions = { tolerance: 30, feather: 12 },
): HTMLCanvasElement {
  const w = source.width;
  const h = source.height;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d', { willReadFrequently: true });
  if (!ctx) throw new Error('2D context unavailable');
  ctx.drawImage(source as CanvasImageSource, 0, 0);
  const img = ctx.getImageData(0, 0, w, h);
  const data = img.data;
  const tol = Math.max(0, Math.min(255, opts.tolerance));
  const fade = Math.max(0, Math.min(255, opts.feather));
  const tolSq = tol * tol;
  const fadeMax = tol + fade;
  for (let i = 0; i < data.length; i += 4) {
    const dr = (data[i]! - target.r);
    const dg = (data[i + 1]! - target.g);
    const db = (data[i + 2]! - target.b);
    const dist = Math.sqrt(dr * dr + dg * dg + db * db);
    if (dist <= tol) {
      data[i + 3] = 0;
    } else if (fade > 0 && dist < fadeMax) {
      const t = (dist - tol) / fade;
      data[i + 3] = Math.round(data[i + 3]! * t);
    }
    void tolSq;
  }
  ctx.putImageData(img, 0, 0);
  return out;
}

/** Encode a canvas to a PNG Blob. */
export async function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob returned null'))),
      'image/png',
    );
  });
}
